import { createAction, props } from '@ngrx/store';
import type {
  ISecapExportDownload,
  ISecapProductDataSource,
  ISecapProductVersionPatch,
  ISecapProjectStatus,
  ISecapRuntimeCatalog,
  ISecapSourceSnapshot,
  ISecapTemplateCompilation,
  ISecapTemplateCompilationFinalizeResult,
  ISecapTemplateCompilationPrepareRequest,
  SecapTemplateCompilationDecision,
  ISecapTemplateCoverage,
  ISecapVersion,
} from '../../../interfaces/secap/ISecapApi';

export const setSecapProjectContext = createAction(
  '[SECAP] Set Project Context',
  props<{ projectUuid: string }>(),
);

export const clearSecapProjectContext = createAction('[SECAP] Clear Project Context');

export const selectSecapVersion = createAction(
  '[SECAP] Select Version',
  props<{ versionUuid: string | null }>(),
);

export const loadSecapProjectStatus = createAction('[SECAP API] Load Project Status');
export const loadSecapProjectStatusSuccess = createAction(
  '[SECAP API] Load Project Status Success',
  props<{ projectUuid: string; status: ISecapProjectStatus }>(),
);
export const loadSecapProjectStatusFailure = createAction(
  '[SECAP API] Load Project Status Failure',
  props<{ projectUuid: string | null; error: string }>(),
);

export const loadSecapVersions = createAction('[SECAP API] Load Versions');
export const loadSecapVersionsSuccess = createAction(
  '[SECAP API] Load Versions Success',
  props<{ projectUuid: string; versions: readonly ISecapVersion[] }>(),
);
export const loadSecapVersionsFailure = createAction(
  '[SECAP API] Load Versions Failure',
  props<{ projectUuid: string | null; error: string }>(),
);

export const patchSecapVersion = createAction(
  '[SECAP API] Patch Version',
  props<{ versionUuid: string; patch: ISecapProductVersionPatch }>(),
);
export const patchSecapVersionSuccess = createAction(
  '[SECAP API] Patch Version Success',
  props<{ projectUuid: string; version: ISecapVersion }>(),
);
export const patchSecapVersionFailure = createAction(
  '[SECAP API] Patch Version Failure',
  props<{ projectUuid: string | null; error: string }>(),
);

export const loadSecapRuntimeCatalog = createAction('[SECAP API] Load Runtime Catalog');
export const loadSecapRuntimeCatalogSuccess = createAction(
  '[SECAP API] Load Runtime Catalog Success',
  props<{ projectUuid: string; catalog: ISecapRuntimeCatalog }>(),
);
export const loadSecapRuntimeCatalogFailure = createAction(
  '[SECAP API] Load Runtime Catalog Failure',
  props<{ projectUuid: string | null; error: string }>(),
);

export const loadSecapTemplateCoverage = createAction('[SECAP API] Load Template Coverage');
export const loadSecapTemplateCoverageSuccess = createAction(
  '[SECAP API] Load Template Coverage Success',
  props<{ projectUuid: string; coverage: ISecapTemplateCoverage }>(),
);
export const loadSecapTemplateCoverageFailure = createAction(
  '[SECAP API] Load Template Coverage Failure',
  props<{ projectUuid: string | null; error: string }>(),
);

export const loadSecapDataSources = createAction('[SECAP API] Load Data Sources');
export const loadSecapDataSourcesSuccess = createAction(
  '[SECAP API] Load Data Sources Success',
  props<{ projectUuid: string; dataSources: readonly ISecapProductDataSource[] }>(),
);
export const loadSecapDataSourcesFailure = createAction(
  '[SECAP API] Load Data Sources Failure',
  props<{ projectUuid: string | null; error: string }>(),
);

export const loadSecapDataSourceDetail = createAction(
  '[SECAP API] Load Data Source Detail',
  props<{ sourceUuid: string }>(),
);
export const loadSecapDataSourceDetailSuccess = createAction(
  '[SECAP API] Load Data Source Detail Success',
  props<{
    projectUuid: string;
    sourceUuid: string;
    source: ISecapProductDataSource;
    snapshots: readonly ISecapSourceSnapshot[];
  }>(),
);
export const loadSecapDataSourceDetailFailure = createAction(
  '[SECAP API] Load Data Source Detail Failure',
  props<{ projectUuid: string | null; sourceUuid: string; error: string }>(),
);

export const loadSecapTemplateCompilations = createAction(
  '[SECAP API] Load Template Compilations',
);
export const loadSecapTemplateCompilationsSuccess = createAction(
  '[SECAP API] Load Template Compilations Success',
  props<{ projectUuid: string; compilations: readonly ISecapTemplateCompilation[] }>(),
);
export const loadSecapTemplateCompilationsFailure = createAction(
  '[SECAP API] Load Template Compilations Failure',
  props<{ projectUuid: string | null; error: string }>(),
);

export const loadSecapTemplateCompilation = createAction(
  '[SECAP API] Load Template Compilation',
  props<{ compilationUuid: string }>(),
);
export const loadSecapTemplateCompilationSuccess = createAction(
  '[SECAP API] Load Template Compilation Success',
  props<{ projectUuid: string; compilation: ISecapTemplateCompilation }>(),
);
export const loadSecapTemplateCompilationFailure = createAction(
  '[SECAP API] Load Template Compilation Failure',
  props<{ projectUuid: string | null; compilationUuid: string; error: string }>(),
);

export const generateSecapTemplateCompilation = createAction(
  '[SECAP API] Generate Template Compilation',
  props<{ request: ISecapTemplateCompilationPrepareRequest }>(),
);
export const uploadSecapTemplateCompilation = createAction(
  '[SECAP API] Upload Template Compilation',
  props<{ file: File; request: ISecapTemplateCompilationPrepareRequest }>(),
);
export const prepareSecapTemplateCompilationSuccess = createAction(
  '[SECAP API] Prepare Template Compilation Success',
  props<{ projectUuid: string; compilation: ISecapTemplateCompilation }>(),
);
export const prepareSecapTemplateCompilationFailure = createAction(
  '[SECAP API] Prepare Template Compilation Failure',
  props<{ projectUuid: string | null; error: string }>(),
);

export const decideSecapTemplateCompilationField = createAction(
  '[SECAP API] Decide Template Compilation Field',
  props<{
    compilationUuid: string;
    compilationFieldUuid: string;
    decision: SecapTemplateCompilationDecision;
  }>(),
);
export const decideSecapTemplateCompilationFieldSuccess = createAction(
  '[SECAP API] Decide Template Compilation Field Success',
  props<{ projectUuid: string; compilation: ISecapTemplateCompilation }>(),
);
export const decideSecapTemplateCompilationFieldFailure = createAction(
  '[SECAP API] Decide Template Compilation Field Failure',
  props<{ projectUuid: string | null; error: string }>(),
);

export const finalizeSecapTemplateCompilation = createAction(
  '[SECAP API] Finalize Template Compilation',
  props<{ compilationUuid: string }>(),
);
export const finalizeSecapTemplateCompilationSuccess = createAction(
  '[SECAP API] Finalize Template Compilation Success',
  props<{ projectUuid: string; result: ISecapTemplateCompilationFinalizeResult }>(),
);
export const finalizeSecapTemplateCompilationFailure = createAction(
  '[SECAP API] Finalize Template Compilation Failure',
  props<{ projectUuid: string | null; error: string }>(),
);

export const downloadSecapTemplateCompilation = createAction(
  '[SECAP API] Download Template Compilation',
  props<{ exportUuid: string }>(),
);
export const downloadSecapTemplateCompilationSuccess = createAction(
  '[SECAP API] Download Template Compilation Success',
  props<{ projectUuid: string; download: ISecapExportDownload }>(),
);
export const downloadSecapTemplateCompilationFailure = createAction(
  '[SECAP API] Download Template Compilation Failure',
  props<{ projectUuid: string | null; error: string }>(),
);
